// react
import { useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import type { FieldError } from 'react-hook-form';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useNavigate } from '@tanstack/react-router';

// material ui icons
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import FilterTiltShiftRoundedIcon from '@mui/icons-material/FilterTiltShiftRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';

// material ui
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// api
import { useTagsGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/tags';
import type { ReadUnitDto } from '@/api/generated/recalc/endpoints/v1/units';
import {
  useUnitsDeleteImageAsyncByidMutation,
  useUnitsDeleteTagAsyncByidtagMutation,
  useUnitsPatchAsyncByidunitMutation,
  useUnitsPostTagAsyncByidnewTagMutation,
} from '@/api/generated/recalc/endpoints/v1/units';
import { useUnitsPutImageAsyncByidfileMutation } from '@/api/generated/recalc/endpoints/v1/units.enhanced';

// shared components
import LocationMap from '@/components/maps/LocationMap';
import { CancelButton, SubmitButton } from '@/components/ui/button';
import {
  EMPTY_ADDRESS_VALUE,
  RHFAddressAutocomplete,
  RHFFieldLabel,
  RHFTextField,
} from '@/components/ui/form';

// feature-components
import DeviceTabs from '@/features/devices/components/shared/DeviceTabs';
import UnitDetailHeader from '@/features/units/components/UnitDetailHeader';
import UnitImageSection from '@/features/units/components/UnitImageSection';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import type { UnitTag } from './createUnitForm.schema';
import {
  toEditUnitFormValues,
  toPatchUnitPayload,
} from './editUnitForm.mappers';
import { createEditUnitFormSchema } from './editUnitForm.schema';
import type { SelectedUnitType } from './unitTypeInput.schema';
import UnitTypeInputsSection from './UnitTypeInputsSection';
import type {
  UnitTypeInputsGetValues,
  UnitTypeInputsSetValue,
} from './UnitTypeInputsSection';

import { zodResolver } from '@hookform/resolvers/zod';

const UNIT_TAB = {
  GENERAL: 0,
  TAGS: 1,
  SENSORS: 2,
  MAP: 3,
  IMAGE: 4,
};

const EditUnitForm = ({
  unit,
  unitType,
  accountId,
}: {
  unit: ReadUnitDto;
  unitType: SelectedUnitType;
  accountId: number | undefined;
}) => {
  const { t } = useTranslation([
    'forms',
    'units-gateways-sensors',
    'validation',
  ]);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [patchUnit, { isLoading: isSaving }] =
    useUnitsPatchAsyncByidunitMutation();
  const [uploadUnitImage, { isLoading: isUploading }] =
    useUnitsPutImageAsyncByidfileMutation();
  const [deleteUnitImage, { isLoading: isDeletingImage }] =
    useUnitsDeleteImageAsyncByidMutation();
  const [addUnitTag] = useUnitsPostTagAsyncByidnewTagMutation();
  const [removeUnitTag] = useUnitsDeleteTagAsyncByidtagMutation();
  const { data: tags } = useTagsGetCollectionAsyncByqueryOptionsQuery(
    { 'Account.AccountId': `eq(${accountId})` },
    { skip: !accountId },
  );
  const tagList = (tags as UnitTag[] | undefined) ?? [];
  const [tagInputValue, setTagInputValue] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasImage = !!unit.ImageHash;
  const recordedDataTypes = unit.RecordedDataTypes ?? [];

  const schema = createEditUnitFormSchema(t, unitType.UnitTypeInputs ?? []);
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { isValid, errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: toEditUnitFormValues(unit, unitType),
    mode: 'onTouched',
  });

  const name = useWatch({ control, name: 'name' });
  const address = useWatch({ control, name: 'address' });
  const hasCoords = address.latitude != null && address.longitude != null;

  const handleClearLocation = () => {
    setValue('address', EMPTY_ADDRESS_VALUE, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !unit.UnitId) return;
    const formData = new FormData();
    formData.append('file', file);
    await uploadUnitImage({
      id: unit.UnitId,
      // The generated arg type declares `body: { file: Blob }`, but the
      // endpoint expects a real multipart/form-data request. Passing a
      // plain object gets JSON.stringify'd by fetchBaseQuery (a File/Blob
      // has no own enumerable properties, so it serializes to `{}`),
      // which the backend rejects with a 400 and an empty response body.
      // Build actual FormData and cast it to satisfy the generated type.
      body: formData as unknown as { file: Blob },
    });
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
    // Cache invalidation via the shared 'Units' tag automatically refetches
    // the unit, so no manual refetch is needed here.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveImage = async () => {
    await deleteUnitImage({ id: unit.UnitId });
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  const onSubmit = handleSubmit(async (values) => {
    const payload = toPatchUnitPayload(values, unit);
    const result = await patchUnit(payload);

    if (!('error' in result)) {
      const existingTags = unit.Tags?.map((tag) => tag.Text) ?? [];
      const tagsToAdd = values.tags.filter(
        (tag) => !existingTags.includes(tag),
      );
      const tagsToRemove = existingTags.filter(
        (tag) => !values.tags.includes(tag),
      );

      await Promise.all([
        ...tagsToAdd.map((text) =>
          addUnitTag({ id: unit.UnitId, createTagDto: { Text: text } }),
        ),
        ...tagsToRemove.map((text) =>
          removeUnitTag({ id: unit.UnitId, tag: text }),
        ),
      ]);
      // Tag errors handled by apiErrorMiddleware (snackbar + App Insights).

      dispatch(
        showSnackbar({
          message: t('units.unitUpdatedSuccess', {
            ns: 'units-gateways-sensors',
            name: values.name,
          }),
          severity: 'success',
        }),
      );
      navigate({ to: '..' });
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  });

  const [activeTab, setActiveTab] = useState(UNIT_TAB.GENERAL);

  return (
    <form onSubmit={onSubmit} noValidate>
      <Stack spacing={2}>
        <UnitDetailHeader unit={unit} />
        <DeviceTabs
          activeTab={activeTab}
          onTabChange={(_, newValue) => setActiveTab(newValue)}
          tabs={[
            {
              label: t('units.unitInformation', {
                ns: 'units-gateways-sensors',
                defaultValue: 'General',
              }),
              testId: 'unit-tab-general',
            },
            {
              label: t('units.tags', { ns: 'units-gateways-sensors' }),
              testId: 'unit-tab-tags',
              icon: <LocalOfferIcon fontSize="small" />,
            },
            {
              label: t('units.unitTypeInputs', {
                ns: 'units-gateways-sensors',
                defaultValue: 'Sensors',
              }),
              testId: 'unit-tab-sensors',
              icon: <FilterTiltShiftRoundedIcon fontSize="small" />,
            },
            {
              label: t('units.unitLocation', {
                ns: 'units-gateways-sensors',
                defaultValue: 'Location',
              }),
              testId: 'unit-tab-map',
              icon: <LocationOnRoundedIcon fontSize="small" />,
            },
            {
              label: t('units.unitImage', {
                ns: 'units-gateways-sensors',
                defaultValue: 'Image',
              }),
              testId: 'unit-tab-image',
              icon: <ImageRoundedIcon fontSize="small" />,
            },
          ]}
        />

        {activeTab === UNIT_TAB.IMAGE && (
          <Stack spacing={2}>
            <Card variant="outlined">
              <UnitImageSection unit={unit} t={t} />
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </Card>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              {hasImage && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleRemoveImage}
                  disabled={isUploading || isDeletingImage}
                  startIcon={
                    isDeletingImage ? (
                      <CircularProgress size={16} />
                    ) : (
                      <DeleteOutlineRoundedIcon />
                    )
                  }
                >
                  {t('common.removeImage', { ns: 'units-gateways-sensors' })}
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || isDeletingImage}
                startIcon={
                  isUploading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <FileUploadRoundedIcon />
                  )
                }
              >
                {hasImage
                  ? t('common.replaceImage', { ns: 'units-gateways-sensors' })
                  : t('common.uploadImage', { ns: 'units-gateways-sensors' })}
              </Button>
            </Box>
          </Stack>
        )}

        {activeTab === UNIT_TAB.TAGS && (
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent>
                <Controller
                  name="tags"
                  control={control}
                  render={({ field }) => (
                    <Box>
                      <RHFFieldLabel>
                        {t('createUnit.tags.label', { ns: 'forms' })}
                      </RHFFieldLabel>
                      <Autocomplete<string, true, false, true>
                        freeSolo
                        multiple
                        disabled={isSaving}
                        options={[...new Set(tagList.map((tag) => tag.Text))]}
                        value={field.value}
                        inputValue={tagInputValue}
                        onInputChange={(_event, newInputValue) => {
                          setTagInputValue(newInputValue);
                        }}
                        onChange={(_event, newValue) => {
                          field.onChange(newValue);
                          setTagInputValue('');
                        }}
                        onBlur={() => {
                          const trimmedValue = tagInputValue.trim();
                          if (
                            trimmedValue &&
                            !field.value.includes(trimmedValue)
                          ) {
                            field.onChange([...field.value, trimmedValue]);
                          }
                          setTagInputValue('');
                          field.onBlur();
                        }}
                        renderValue={(value, getItemProps) =>
                          value.map((option, index) => {
                            const { key, ...tagProps } = getItemProps({
                              index,
                            });
                            return (
                              <Chip
                                key={key}
                                icon={
                                  <LocalOfferIcon
                                    sx={{ color: 'white !important' }}
                                  />
                                }
                                label={option}
                                sx={{
                                  borderRadius: 1,
                                  backgroundColor: 'info.main',
                                  color: 'white',
                                  '& .MuiChip-label': {
                                    color: 'white',
                                  },
                                }}
                                size="small"
                                {...tagProps}
                              />
                            );
                          })
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={t('createUnit.tags.placeholder', {
                              ns: 'forms',
                            })}
                            helperText={t('createUnit.tags.helperText', {
                              ns: 'forms',
                            })}
                            size="small"
                          />
                        )}
                      />
                    </Box>
                  )}
                />
              </CardContent>
            </Card>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <CancelButton
                onClick={() => navigate({ to: '..' })}
                disabled={isSaving}
              >
                {t('common.cancel')}
              </CancelButton>
              <SubmitButton loading={isSaving} disabled={isSaving || !isValid}>
                {t('common.save')}
              </SubmitButton>
            </Box>
          </Stack>
        )}

        {activeTab === UNIT_TAB.GENERAL && (
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <RHFTextField
                    name="name"
                    control={control}
                    required
                    fullWidth
                    data-testid="edit-unit-name-input"
                    label={t('editUnit.name.label', { ns: 'forms' })}
                    placeholder={t('editUnit.name.placeholder', {
                      ns: 'forms',
                    })}
                    helperText={t('editUnit.name.helperText', {
                      ns: 'forms',
                    })}
                    disabled={isSaving}
                  />

                  <RHFTextField
                    name="description"
                    control={control}
                    fullWidth
                    multiline
                    rows={2}
                    label={t('editUnit.description.label', { ns: 'forms' })}
                    placeholder={t('editUnit.description.placeholder', {
                      ns: 'forms',
                    })}
                    helperText={t('editUnit.description.helperText', {
                      ns: 'forms',
                    })}
                    disabled={isSaving}
                  />
                </Stack>
              </CardContent>
            </Card>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <CancelButton
                onClick={() => navigate({ to: '..' })}
                disabled={isSaving}
              >
                {t('common.cancel')}
              </CancelButton>
              <SubmitButton loading={isSaving} disabled={isSaving || !isValid}>
                {t('common.save')}
              </SubmitButton>
            </Box>
          </Stack>
        )}

        {activeTab === UNIT_TAB.SENSORS && (
          <Stack spacing={2}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={2}>
                  <UnitTypeInputsSection
                    key={unitType.UnitTypeId}
                    control={control}
                    getValues={getValues as UnitTypeInputsGetValues}
                    setValue={setValue as unknown as UnitTypeInputsSetValue}
                    selectedUnitType={unitType}
                    recordedDataTypes={recordedDataTypes}
                  />
                  {(errors.unitTypeInputs as FieldError | undefined)
                    ?.message ? (
                    <FormHelperText error>
                      {
                        (errors.unitTypeInputs as FieldError | undefined)
                          ?.message
                      }
                    </FormHelperText>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <CancelButton
                onClick={() => navigate({ to: '..' })}
                disabled={isSaving}
              >
                {t('common.cancel')}
              </CancelButton>
              <SubmitButton loading={isSaving} disabled={isSaving || !isValid}>
                {t('common.save')}
              </SubmitButton>
            </Box>
          </Stack>
        )}

        {activeTab === UNIT_TAB.MAP && (
          <Stack spacing={2}>
            <Card variant="outlined">
              {hasCoords ? (
                <LocationMap
                  zoom={15}
                  pins={[
                    {
                      latitude: address.latitude as number,
                      longitude: address.longitude as number,
                      label: name || unit.Name,
                    },
                  ]}
                />
              ) : (
                <Box
                  sx={{
                    height: 400,
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    color: 'text.disabled',
                  }}
                >
                  <RoomOutlinedIcon sx={{ fontSize: 40 }} />
                  <Typography variant="body2">
                    {t('gateways.noLocationSet', {
                      ns: 'units-gateways-sensors',
                    })}
                  </Typography>
                  <Typography variant="caption">
                    {t('gateways.searchAddressHint', {
                      ns: 'units-gateways-sensors',
                    })}
                  </Typography>
                </Box>
              )}
            </Card>
            <Card variant="outlined">
              <CardContent>
                <RHFAddressAutocomplete
                  name="address"
                  control={control}
                  required
                  disabled={isSaving}
                  fullWidth
                  size="small"
                />
              </CardContent>
            </Card>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="text"
                color="error"
                onClick={handleClearLocation}
                disabled={isSaving || !hasCoords}
                sx={{ visibility: hasCoords ? 'visible' : 'hidden' }}
              >
                {t('gateways.clearLocation', { ns: 'units-gateways-sensors' })}
              </Button>
              <Box gap={1} display="flex">
                <CancelButton
                  onClick={() => navigate({ to: '..' })}
                  disabled={isSaving}
                >
                  {t('common.cancel')}
                </CancelButton>
                <SubmitButton
                  loading={isSaving}
                  disabled={isSaving || !isValid}
                  data-testid="edit-unit-save-button"
                >
                  {t('common.save')}
                </SubmitButton>
              </Box>
            </Box>
          </Stack>
        )}
      </Stack>
    </form>
  );
};

export default EditUnitForm;
